import { Link, useTheme } from '@material-ui/core';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { TagFilterInputGq } from '../../../../graphql/projects/models/TagFilterInputGq';
import { TagGq } from '../../../../graphql/projects/models/TagGq';
import { TagGroupGq } from '../../../../graphql/projects/models/TagGroupGq';
import VoxelMenuItemContainer from '../../../voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelAdvancedTextSearch from '../../../voxel/Search/VoxelAdvancedTextSearch';
import VoxelTooltipShort from '../../../voxel/Tooltip/VoxelTooltipShort';
import { HeadCellImplementationProps } from '../KeywordTable/HeadCells/models';
import HeadCell from './HeadCell';
import HeadCellStandardOptions from './HeadCellStandardOptions';
import { useClasses } from './HeadCellTagGroup.styles';
import HeadCellTagSelection from './HeadCellTagSelection';
import VoxelPopoverMenuItem from '../../../voxel/PopoverMenu/VoxelPopoverMenuItem';
import VoxelMenuDivider from '../../../voxel/PopoverMenu/VoxelMenuDivider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons';

export interface HeadCellTagGroupProps extends HeadCellImplementationProps {
  state?: TagFilterInputGq[];
  setState?: (state: TagFilterInputGq[]) => void;
  tagGroup: TagGroupGq;
  shownTagPanelId?: number;
  setShownTagPanelId?: (id?: number) => void;
}

const HeadCellTagGroup: React.FC<HeadCellTagGroupProps> = ({
  state,
  setState,
  tagGroup,
  basicConfig,
  basicState,
  shownTagPanelId,
  setShownTagPanelId,
}) => {
  // Tags
  const tagsMap = useMemo(() => {
    const result: Record<number, TagGq> = {};
    tagGroup.tags.forEach((t) => {
      result[t.id] = t;
    });
    return result;
  }, [tagGroup]);

  // Filter
  const filterValue = useMemo(
    () => state.filter((tf) => tf.tagGroupId === tagGroup.id),
    [state, tagGroup.id]
  );

  const filterOptions: TagFilterInputGq[] = useMemo(
    () =>
      _.chain([
        ...tagGroup.tags.map(
          (tag): TagFilterInputGq => ({ tagId: tag.id, tagGroupId: tagGroup.id })
        ),
        {
          tagId: undefined,
          tagGroupId: tagGroup.id,
        },
      ])
        .orderBy([(t) => t.tagId !== undefined, (t) => t.tagGroupId, (t) => tagsMap[t.tagId]?.name])
        .value(),
    [tagGroup]
  );

  const handleFilterChange = useCallback(
    (value: TagFilterInputGq, checked: boolean) => {
      const tagFilters = state.filter(
        (tf) => !(tf.tagGroupId === value.tagGroupId && tf.tagId === value.tagId)
      );
      checked && tagFilters.push(value);
      setState && setState(tagFilters);
    },
    [state]
  );

  const selectAllTags = useCallback(() => {
    const tagFilters = state.filter((tF) => tF.tagGroupId != tagGroup.id);
    tagGroup.tags.forEach((tag) => tagFilters.push({ tagId: tag.id, tagGroupId: tagGroup.id }));
    setState && setState(tagFilters);
  }, [tagGroup, state]);

  const resetTagFilter = useCallback(() => {
    const tagFilters = state.filter((tF) => tF.tagGroupId != tagGroup.id);
    setState && setState(tagFilters);
  }, [tagGroup, state]);

  // Tag search
  const [tagSearchText, setTagSearchText] = useState('');
  const displayedTags = useMemo(() => {
    if (!tagSearchText.length) {
      return filterOptions;
    }
    const words = tagSearchText.split(' ').map((word) => word.toLowerCase());
    return filterOptions.filter((option) =>
      words.every((word) => (tagsMap[option.tagId]?.name ?? 'N/A').toLowerCase().includes(word))
    );
  }, [tagSearchText, filterOptions]);

  // styling
  const theme = useTheme();
  const classes = useClasses(theme);

  return (
    <HeadCell
      hasFilter={!!filterValue.length}
      align="left"
      basicConfig={basicConfig}
      basicState={basicState}
    >
      <VoxelMenuItemContainer size="md">
        {setShownTagPanelId && (
          <VoxelPopoverMenuItem
            icon={<FontAwesomeIcon icon={faPen} />}
            title="Open tag panel"
            selected={shownTagPanelId === tagGroup.id}
            onClick={() =>
              setShownTagPanelId(shownTagPanelId === tagGroup.id ? undefined : tagGroup.id)
            }
          />
        )}
        <HeadCellStandardOptions basicConfig={basicConfig} basicState={basicState} />
        <div className="px-2 flex flex-row gap-2 pt-2 pb-2">
          <Link
            component="button"
            variant="caption"
            onClick={selectAllTags}
            className="!underline !decoration-1 disabled:!no-underline disabled:text-gray-400  disabled:cursor-default"
            disabled={filterValue.length === tagGroup.tags.length}
          >
            Select all
          </Link>
          <VoxelTooltipShort message="clear selection" disabled={!filterValue.length}>
            <Link
              component="button"
              variant="caption"
              onClick={resetTagFilter}
              className="!underline !decoration-1 disabled:!no-underline disabled:text-gray-400 disabled:cursor-default"
              disabled={!filterValue.length}
            >
              Clear
            </Link>
          </VoxelTooltipShort>
        </div>

        <div className="px-2 pb-3">
          <VoxelAdvancedTextSearch
            autoFocus
            onChange={(event) => setTagSearchText(event.target.value)}
            value={tagSearchText}
          />
        </div>
        <div className="max-h-96 overflow-y-scroll">
          {displayedTags.map((displayedTag) => (
            <HeadCellTagSelection
              key={displayedTag.tagId ?? -1}
              optionDisplayName={tagsMap[displayedTag.tagId]?.name ?? 'N/A'}
              option={displayedTag}
              handleFilterChange={handleFilterChange}
              filter={filterValue}
            />
          ))}
        </div>
      </VoxelMenuItemContainer>
    </HeadCell>
  );
};

export default HeadCellTagGroup;
